import React, { useCallback, useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FlaskConical, Play, Loader2, Download, ClipboardList, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { useApi, useAuth } from '@/contexts/AuthContext';

const C = { navy: '#0D1B2A', slate: '#5C6B7A', green: '#1D9E75', amber: '#E8A020', red: '#E24B4A', blue: '#1A4C7C' };
const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const STATUS = {
  priced: { label: 'Priced', color: C.green, bg: '#E7F6EF' },
  referred: { label: 'Referred', color: C.amber, bg: '#FDF7EC' },
  blocked: { label: 'Blocked', color: C.red, bg: '#FDF1F1' },
  missing_fields: { label: 'Missing fields', color: C.red, bg: '#FDF1F1' },
  unmapped: { label: 'Unmapped class', color: C.slate, bg: 'rgba(136,150,168,0.12)' },
  failed: { label: 'Failed', color: C.red, bg: '#FDF1F1' },
};

function Chip({ status, count }) {
  const s = STATUS[status] || STATUS.failed;
  return (
    <div className="px-3 py-2 rounded-sm" style={{ background: s.bg }} data-testid={`val-chip-${status}`}>
      <p className="text-xl font-bold font-mono leading-none" style={{ color: s.color }}>{count}</p>
      <p className="text-[10px] uppercase tracking-wider mt-1" style={{ color: s.color }}>{s.label}</p>
    </div>
  );
}

function StatusTag({ status }) {
  const s = STATUS[status] || STATUS.failed;
  return (
    <span className="px-1.5 py-0.5 rounded-sm text-[9px] font-bold uppercase whitespace-nowrap"
      style={{ background: s.bg, color: s.color }} data-testid={`val-status-${status}`}>{s.label}</span>
  );
}

function BasisNote({ basis, note }) {
  const hard = basis === 'seeded' || basis === 'none';
  return (
    <div className="p-3 rounded-sm mb-4" data-testid="val-basis-note"
      style={{ background: hard ? '#FDF7EC' : 'rgba(26,76,124,0.05)', borderLeft: `3px solid ${hard ? C.amber : C.blue}` }}>
      <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: hard ? C.amber : C.blue }}>
        Premium of record: {basis}
      </p>
      <p className="text-[11px] mt-1" style={{ color: C.slate }}>{note}</p>
    </div>
  );
}

function CoverageTable({ audit }) {
  return (
    <table className="w-full text-[11px]" data-testid="val-coverage-table">
      <thead><tr className="text-left text-[9px] uppercase" style={{ color: C.slate }}>
        <th className="py-1 pr-2">Reference</th><th className="py-1 pr-2">Insured</th>
        <th className="py-1 pr-2">Class as recorded</th><th className="py-1 pr-2">Resolved</th>
        <th className="py-1 pr-2">Coverage</th><th className="py-1 pr-2">Rateable</th>
        <th className="py-1">What is missing</th>
      </tr></thead>
      <tbody>
        {audit.rows.map((r) => (
          <tr key={`${r.kind}-${r.ref}`} className="border-t align-top" style={{ borderColor: 'rgba(136,150,168,0.12)' }}
            data-testid={`val-audit-row-${r.ref}`}>
            <td className="py-1.5 pr-2 font-mono" style={{ color: C.navy }}>
              {r.ref?.length > 14 ? `${r.ref.slice(0, 8)}…` : r.ref}
              <span className="ml-1 text-[9px] uppercase" style={{ color: C.slate }}>{r.kind}</span>
            </td>
            <td className="py-1.5 pr-2" style={{ color: C.navy }}>{r.business_name || '—'}</td>
            <td className="py-1.5 pr-2" style={{ color: C.slate }}>{r.class_label || '—'}</td>
            <td className="py-1.5 pr-2 font-mono text-[10px]" style={{ color: r.class_id ? C.navy : C.red }}>
              {r.class_id || 'unmapped'}
            </td>
            <td className="py-1.5 pr-2 font-mono" style={{ color: r.audit.coverage_pct >= 50 ? C.green : C.amber }}>
              {r.audit.coverage_pct}%
              <span className="ml-1 text-[9px]" style={{ color: C.slate }}>
                {r.audit.fields_present}/{r.audit.fields_declared}
              </span>
            </td>
            <td className="py-1.5 pr-2">
              {r.ready_to_rate
                ? <span className="text-[9px] font-bold uppercase" style={{ color: C.green }} data-testid={`val-ready-${r.ref}`}>yes</span>
                : <span className="text-[9px] font-bold uppercase" style={{ color: C.red }} data-testid={`val-notready-${r.ref}`}>no</span>}
            </td>
            <td className="py-1.5 text-[10px]" style={{ color: C.slate }}>
              {!r.gate.quotable && (
                <p style={{ color: C.red }} data-testid={`val-blocker-${r.ref}`}>
                  {r.gate.blockers.map((b) => b.code).join(', ')} — {r.gate.blockers[0]?.detail}
                </p>
              )}
              {!r.audit.critical_satisfied && r.audit.critical_any_of?.length > 0 && (
                <p style={{ color: C.red }} data-testid={`val-critical-${r.ref}`}>
                  nothing to rate on — needs one of: {r.audit.critical_any_of.join(', ')}
                </p>
              )}
              {r.audit.required_missing?.length > 0 && (
                <p>required not declared: {r.audit.required_missing.join(', ')}</p>
              )}
              {r.audit.identity_missing?.length > 0 && (
                <p>identity not on file: {r.audit.identity_missing.join(', ')}</p>
              )}
              {r.ready_to_rate && r.audit.required_missing?.length === 0 && <span>—</span>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function RunTable({ run }) {
  return (
    <table className="w-full text-[11px]" data-testid="val-run-table">
      <thead><tr className="text-left text-[9px] uppercase" style={{ color: C.slate }}>
        <th className="py-1 pr-2">Reference</th><th className="py-1 pr-2">Insured</th>
        <th className="py-1 pr-2">Class</th><th className="py-1 pr-2">Outcome</th>
        <th className="py-1 pr-2 text-right">Engine</th><th className="py-1 pr-2 text-right">Of record</th>
        <th className="py-1 pr-2 text-right">Variance</th><th className="py-1">Why</th>
      </tr></thead>
      <tbody>
        {run.rows.map((r) => (
          <tr key={`${r.kind}-${r.ref}`} className="border-t align-top" style={{ borderColor: 'rgba(136,150,168,0.12)' }}
            data-testid={`val-run-row-${r.ref}`}>
            <td className="py-1.5 pr-2 font-mono" style={{ color: C.navy }}>
              {r.ref?.length > 14 ? `${r.ref.slice(0, 8)}…` : r.ref}
            </td>
            <td className="py-1.5 pr-2" style={{ color: C.navy }}>{r.business_name || '—'}</td>
            <td className="py-1.5 pr-2 font-mono text-[10px]" style={{ color: C.slate }}>{r.class_id || r.class_label || '—'}</td>
            <td className="py-1.5 pr-2"><StatusTag status={r.status} /></td>
            <td className="py-1.5 pr-2 font-mono text-right" style={{ color: r.computed_premium > 0 ? C.navy : C.slate }}>
              {r.computed_premium > 0 ? money(r.computed_premium) : '—'}
            </td>
            <td className="py-1.5 pr-2 font-mono text-right" style={{ color: C.slate }}>
              {r.recorded_premium > 0 ? money(r.recorded_premium) : '—'}
            </td>
            <td className="py-1.5 pr-2 font-mono text-right"
              style={{ color: r.variance_pct === null ? C.slate : Math.abs(r.variance_pct) > 25 ? C.red : C.green }}
              data-testid={`val-variance-${r.ref}`}>
              {r.variance_pct === null ? '—' : `${r.variance_pct > 0 ? '+' : ''}${r.variance_pct}%`}
            </td>
            <td className="py-1.5 text-[10px]" style={{ color: C.slate }}>{r.reason || '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function ValidationHarnessPage() {
  const api = useApi();
  const { user } = useAuth();
  const isAdmin = ['platform_owner', 'mga_admin'].includes(user?.role);

  const [cohorts, setCohorts] = useState([]);
  const [cohort, setCohort] = useState('burmac');
  const [limit, setLimit] = useState(10);
  const [audit, setAudit] = useState(null);
  const [auditing, setAuditing] = useState(false);
  const [run, setRun] = useState(null);
  const [running, setRunning] = useState(false);
  const [past, setPast] = useState([]);

  const loadRuns = useCallback(() => {
    api.get('/validation/runs?limit=10').then((r) => {
      setPast(r.data.runs || []);
      if ((r.data.cohorts || []).length) setCohorts(r.data.cohorts);
    }).catch(() => {});
  }, [api]);

  useEffect(() => { loadRuns(); }, [loadRuns]);

  const loadAudit = useCallback(async () => {
    setAuditing(true); setRun(null);
    try {
      const { data } = await api.get(`/validation/field-coverage?cohort=${cohort}&limit=${limit}`);
      setAudit(data);
      toast.success(`${data.records} record(s) audited — ${data.ready_to_rate} ready to rate, ${data.not_ready} not`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'The field audit could not run');
    } finally { setAuditing(false); }
  }, [api, cohort, limit]);

  useEffect(() => { loadAudit(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [cohort]);

  const startRun = async () => {
    setRunning(true);
    try {
      const { data } = await api.post('/validation/runs', { cohort, limit: Number(limit) });
      setRun(data);
      toast.success(`${data.summary.records} record(s) run — ${data.summary.priced} priced, ${data.summary.referred} referred`);
      loadRuns();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'The validation run failed');
    } finally { setRunning(false); }
  };

  const openRun = async (run_id) => {
    try {
      const { data } = await api.get(`/validation/runs/${run_id}`);
      setRun(data);
    } catch { toast.error('That run could not be opened'); }
  };

  const exportRun = async (run_id) => {
    try {
      const res = await api.get(`/validation/runs/${run_id}/export.csv`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url; a.download = `validation_${run_id.slice(0, 8)}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('The export could not be downloaded'); }
  };

  const active = cohorts.find((c) => c.id === cohort);

  return (
    <div className="space-y-6" data-testid="validation-harness-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" style={{ color: C.navy, fontFamily: "'IBM Plex Sans'" }}>
            Validation Harness
          </h1>
          <p className="text-sm mt-1" style={{ color: C.slate }}>
            The field audit first, then the pricing run — so a mapping gap is never read as a rating failure
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select value={cohort} onChange={(e) => setCohort(e.target.value)} data-testid="val-cohort-select"
            className="h-8 px-2 text-[11px] rounded-sm border bg-white" style={{ borderColor: 'rgba(136,150,168,0.3)', color: C.navy }}>
            {(cohorts.length ? cohorts : [{ id: 'burmac', label: 'Burmac seeded book' }]).map((c) => (
              <option key={c.id} value={c.id}>{c.label}</option>
            ))}
          </select>
          <Input type="number" value={limit} min={1} max={50} onChange={(e) => setLimit(e.target.value)}
            className="w-16 h-8 text-[11px] rounded-sm" data-testid="val-limit-input" />
          <Button size="sm" variant="outline" onClick={loadAudit} disabled={auditing}
            className="h-8 text-[11px] rounded-sm" data-testid="val-audit-btn">
            {auditing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <ClipboardList className="w-3 h-3 mr-1" />}
            Re-run field audit
          </Button>
          {isAdmin && (
            <Button size="sm" onClick={startRun} disabled={running || !audit}
              className="h-8 text-[11px] rounded-sm text-white" style={{ background: C.blue }} data-testid="val-run-btn">
              {running ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Play className="w-3 h-3 mr-1" />}
              Run validation
            </Button>
          )}
        </div>
      </div>

      {/* ── Field audit ── */}
      <Card className="p-5 rounded-sm bg-white" style={{ border: '1px solid rgba(136,150,168,0.2)' }} data-testid="val-audit-card">
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <ClipboardList className="w-4 h-4" style={{ color: C.blue }} />
          <h3 className="text-sm font-semibold" style={{ color: C.navy }}>Step 1 — Field coverage audit</h3>
          {audit && (
            <>
              <span className="px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase" data-testid="val-ready-count"
                style={{ background: '#E7F6EF', color: C.green }}>{audit.ready_to_rate} ready to rate</span>
              <span className="px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase" data-testid="val-notready-count"
                style={{ background: audit.not_ready ? '#FDF1F1' : '#E7F6EF', color: audit.not_ready ? C.red : C.green }}>
                {audit.not_ready} not ready
              </span>
            </>
          )}
        </div>
        {!audit ? (
          <p className="text-[11px]" style={{ color: C.slate }} data-testid="val-audit-empty">
            {auditing ? 'Reading the cohort…' : 'No audit yet for this cohort.'}
          </p>
        ) : (
          <>
            <BasisNote basis={audit.premium_basis} note={audit.cohort_note} />
            <p className="text-[11px] mb-3" style={{ color: C.slate }}>{audit.note}</p>
            <CoverageTable audit={audit} />
            {audit.most_missed?.length > 0 && (
              <div className="mt-4 pt-3 border-t" style={{ borderColor: 'rgba(136,150,168,0.15)' }}>
                <p className="text-[9px] uppercase tracking-wider mb-2" style={{ color: C.slate }}>Most missed declared fields</p>
                <div className="flex flex-wrap gap-1.5" data-testid="val-most-missed">
                  {audit.most_missed.map(([field, count]) => (
                    <span key={field} className="px-2 py-1 rounded-sm text-[10px] font-mono"
                      style={{ background: 'rgba(226,75,74,0.07)', color: C.red }}>{field} · {count}</span>
                  ))}
                </div>
              </div>
            )}
            <div className="mt-4 pt-3 border-t grid gap-2 md:grid-cols-2" style={{ borderColor: 'rgba(136,150,168,0.15)' }}>
              {audit.by_class.map((c) => (
                <div key={c.class_id || c.name} className="p-2 rounded-sm" style={{ background: 'rgba(136,150,168,0.06)' }}
                  data-testid={`val-audit-class-${c.class_id || 'unmapped'}`}>
                  <p className="text-[11px] font-semibold" style={{ color: C.navy }}>{c.name}</p>
                  <p className="text-[10px]" style={{ color: C.slate }}>
                    {c.records} record(s) · {c.ready_to_rate} rateable · {c.coverage_pct}% field coverage
                    {c.quotable === false && <span style={{ color: C.red }}> · class not quotable</span>}
                  </p>
                  {c.missing_fields?.length > 0 && (
                    <p className="text-[10px] font-mono mt-0.5" style={{ color: C.red }}>
                      missing: {c.missing_fields.map(([f, n]) => `${f}×${n}`).join(', ')}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </Card>

      {/* ── Validation run ── */}
      <Card className="p-5 rounded-sm bg-white" style={{ border: '1px solid rgba(136,150,168,0.2)' }} data-testid="val-run-card">
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <FlaskConical className="w-4 h-4" style={{ color: C.blue }} />
          <h3 className="text-sm font-semibold" style={{ color: C.navy }}>Step 2 — Pricing run</h3>
          {run && (
            <>
              <span className="text-[10px] font-mono" style={{ color: C.slate }} data-testid="val-run-id">{run.run_id?.slice(0, 8)}</span>
              <Button size="sm" variant="ghost" onClick={() => exportRun(run.run_id)}
                className="h-6 px-2 text-[10px] rounded-sm" style={{ color: C.blue }} data-testid="val-export-btn">
                <Download className="w-3 h-3 mr-1" />Export CSV
              </Button>
            </>
          )}
        </div>
        {!run ? (
          <p className="text-[11px]" style={{ color: C.slate }} data-testid="val-run-empty">
            {isAdmin
              ? 'Run the audit first, then price the cohort. Nothing is written to a policy — a run is a read of the book through uw_core.'
              : 'Runs are started by an owner or MGA admin. Past runs are listed below and can be opened.'}
          </p>
        ) : (
          <>
            <BasisNote basis={run.premium_basis} note={run.cohort_note} />
            <div className="flex flex-wrap gap-2 mb-4" data-testid="val-summary-chips">
              <Chip status="priced" count={run.summary.priced} />
              <Chip status="referred" count={run.summary.referred} />
              <Chip status="blocked" count={run.summary.blocked} />
              <Chip status="missing_fields" count={run.summary.missing_fields} />
              <Chip status="unmapped" count={run.summary.unmapped} />
              <Chip status="failed" count={run.summary.failed} />
              <div className="px-3 py-2 rounded-sm" style={{ background: 'rgba(26,76,124,0.06)' }} data-testid="val-variance-summary">
                <p className="text-xl font-bold font-mono leading-none" style={{ color: C.blue }}>
                  {run.summary.variance.median_abs_pct === null ? '—' : `${run.summary.variance.median_abs_pct}%`}
                </p>
                <p className="text-[10px] uppercase tracking-wider mt-1" style={{ color: C.blue }}>
                  median variance · {run.summary.variance.measured_on} measured
                </p>
              </div>
            </div>
            {run.summary.variance.measured_on === 0 && (
              <p className="text-[11px] mb-3 flex items-start gap-1.5" style={{ color: C.amber }} data-testid="val-no-variance-note">
                <AlertTriangle className="w-3.5 h-3.5 mt-px shrink-0" />
                No record in this cohort carries a premium anybody charged, so no variance is reported. The priced /
                referred / blocked split and the named missing fields are the findings.
              </p>
            )}
            <RunTable run={run} />
            <div className="mt-4 pt-3 border-t grid gap-2 md:grid-cols-2" style={{ borderColor: 'rgba(136,150,168,0.15)' }}>
              {run.summary.by_class.map((c) => (
                <div key={c.class_id || c.name} className="p-2 rounded-sm" style={{ background: 'rgba(136,150,168,0.06)' }}
                  data-testid={`val-run-class-${c.class_id || 'unmapped'}`}>
                  <p className="text-[11px] font-semibold" style={{ color: C.navy }}>{c.name}</p>
                  <p className="text-[10px]" style={{ color: C.slate }}>
                    {Object.entries(c.statuses).map(([s, n]) => `${n} ${(STATUS[s] || {}).label || s}`).join(' · ')}
                    {c.median_variance_pct !== null && ` · median variance ${c.median_variance_pct}%`}
                  </p>
                  {c.missing_fields?.length > 0 && (
                    <p className="text-[10px] font-mono mt-0.5" style={{ color: C.red }}>
                      missing: {c.missing_fields.map(([f, n]) => `${f}×${n}`).join(', ')}
                    </p>
                  )}
                </div>
              ))}
            </div>
            <p className="text-[10px] mt-3" style={{ color: C.slate }}>{run.note}</p>
          </>
        )}
      </Card>

      {/* ── Past runs ── */}
      {past.length > 0 && (
        <Card className="p-5 rounded-sm bg-white" style={{ border: '1px solid rgba(136,150,168,0.2)' }} data-testid="val-past-runs">
          <h3 className="text-sm font-semibold mb-3" style={{ color: C.navy }}>Past runs</h3>
          <div className="space-y-1">
            {past.map((r) => (
              <div key={r.run_id} className="flex flex-wrap items-center gap-3 py-1.5 border-t text-[11px]"
                style={{ borderColor: 'rgba(136,150,168,0.12)' }} data-testid={`val-past-run-${r.run_id}`}>
                <span className="font-mono" style={{ color: C.slate }}>{r.created_at?.slice(0, 16).replace('T', ' ')}</span>
                <span style={{ color: C.navy }}>{r.label}</span>
                <span className="flex items-center gap-1" style={{ color: C.green }}>
                  <CheckCircle2 className="w-3 h-3" />{r.summary?.priced}/{r.summary?.records} priced
                </span>
                <span style={{ color: C.slate }}>by {r.by || 'unknown'}</span>
                <button onClick={() => openRun(r.run_id)} className="ml-auto px-2 py-0.5 rounded-sm text-[10px] font-semibold border"
                  style={{ borderColor: 'rgba(136,150,168,0.3)', color: C.blue }} data-testid={`val-open-run-${r.run_id}`}>Open</button>
                <button onClick={() => exportRun(r.run_id)} className="px-2 py-0.5 rounded-sm text-[10px] font-semibold border"
                  style={{ borderColor: 'rgba(136,150,168,0.3)', color: C.blue }} data-testid={`val-export-run-${r.run_id}`}>CSV</button>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
